import React, { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
// import { Scrollbars } from "react-custom-scrollbars-2";

import {
  GAQuicktalesTableHeadCells,
  GeneralAdminRoutes,
  getComparator,
  stableSort,
} from "../../../../../constants";
import { TableRowSection } from "../components";
import TablePaginationSection from "../../../../../common/TablePaginationSection";
import ImagePreviewModal from "../../../../../common/ImagePreviewModal";
import AudioModal from "../../../../../common/AudioModal";
import { useQuickTalesContext } from "../../../../../providers/OrganizationAdmin/QuickTalesProvider";
import CommonTableHead from "../../../../../common/CommonTableHead";

export default function QuickTalesTable({
  selected,
  setSelected,
  quickTalesData,
  organizationsData = [],
}) {

  const {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    resolveUsersForTales,
  } = useQuickTalesContext();
  const navigate = useNavigate();
  // const scrollbarsRef = useRef(null);

  // Default to newest-first to match the server-side createdAt DESC ordering,
  // so the first page shows without a client-side re-sort flash.
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("createdAt");

  // const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(8);
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Initialize column widths based on GAQuicktalesTableHeadCells
  const initializeColumnWidths = () => {
    const widths = {};
    GAQuicktalesTableHeadCells.forEach(cell => {
      widths[cell.id] = cell.width || 150;
    });
    return widths;
  };
  
  const [columnWidths, setColumnWidths] = useState(initializeColumnWidths);

  const handleColumnResize = (columnId, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: newWidth
    }));
  };

  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(order === "asc" ? "desc" : "asc");
    // setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page - 1 > 0
      ? Math.max(0, (1 + page - 1) * rowsPerPage - quickTalesData.length)
      : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(quickTalesData, getComparator(order, orderBy)).slice(
        (page - 1) * rowsPerPage,
        (page - 1) * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, quickTalesData]
  );

  // Lazily resolve creator/narrator names for only the rows currently visible
  // on the page. The provider stores rows without resolving these up front (to
  // keep initial load fast), so we batch-fetch the referenced users here as the
  // admin pages/sorts/filters. Guarded by an id-signature so we don't re-fetch
  // rows we've already resolved.
  const resolvedSigRef = useRef("");
  useEffect(() => {
    if (typeof resolveUsersForTales !== "function") return;
    const rowsNeedingResolution = visibleRows.filter(
      (r) =>
        (r?.createdBy && !r?.createdByData) ||
        (r?.narrator && !r?.narratorData)
    );
    if (rowsNeedingResolution.length === 0) return;

    const sig = rowsNeedingResolution.map((r) => r.id).join(",");
    if (sig === resolvedSigRef.current) return;
    resolvedSigRef.current = sig;

    resolveUsersForTales(rowsNeedingResolution);
  }, [visibleRows, resolveUsersForTales]);


  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = visibleRows.map((n) => n.id);
      // const newSelected = quickTalesData.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClickRow = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // console.log(Math.ceil(2.1))

  return (
    <Box sx={mainBoxContainerStyles}>
      <Paper sx={tablePaperContainerStyles}>
        <TableContainer
          style={{ border: "none", boxShadow: "none", maxHeight: "calc(90vh - 220px)", maxWidth: "calc(90vw - 120px)" }}
        >
          <Table
            stickyHeader
            aria-labelledby="tableTitle"
          >
            <CommonTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={visibleRows.length}
              tableHeadCells={GAQuicktalesTableHeadCells}
              columnWidths={columnWidths}
              onColumnResize={handleColumnResize}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;
                const numberType = index % 2 == 0;

                return (
                  <TableRowSection
                    isItemSelected={isItemSelected}
                    row={row}
                    handleClickRow={handleClickRow}
                    numberType={numberType}
                    labelId={labelId}
                    key={index}
                    handleImagePreviewModalOpen={handleImagePreviewModalOpen}
                    handleAudioModalOpen={handleAudioModalOpen}
                    setSelectedAudioFile={setSelectedAudioFile}
                    setSelectedImage={setSelectedImage}
                    columnWidths={columnWidths}
                    organizationsData={organizationsData}

                    handleNavigateToDetails={() => {
                      // navigate(GeneralAdminRoutes.quicktaleDetails + row.id);
                      navigate(GeneralAdminRoutes.quicktaleDetails, {
                        state: { quicktaleDetails: row },
                      });
                    }}
                  />
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
              {visibleRows.length === 0  && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No results found
                  </TableCell>
                </TableRow>
              ) }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePaginationSection
        count={Math.ceil(quickTalesData.length / rowsPerPage)}
        page={page}
        total={quickTalesData.length ?? 0}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        image={selectedImage && selectedImage}
        // image={images.tableDummyImage}
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        audioFile={selectedAudioFile && selectedAudioFile}
        // audioFile={audios.audioSample}
      />
    </Box>
  );
}

const mainBoxContainerStyles = {
  width: "100%",
  paddingRight: 1,
  border: "none",
  boxShadow: "none",
};

const tablePaperContainerStyles = {
  maxWidth: "100%",
  mb: 1,
  border: "none",
  boxShadow: "none",
  mt: 1,
};
